import { ApolloClient, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';
import { deviceDetect } from 'react-device-detect';
import cookie from 'react-cookies';
import { getToken } from '../../utils';
import { config } from '../Constants/AppConstants';
import { getBrowserPlatform, getBrowserVersion } from '../../utils/getBrowser';
import { handleApolloOperationError } from './authErrorHandling';

const SENSITIVE_KEYS = new Set([
  'password',
  'confirmPassword',
  'currentPassword',
  'newPassword',
  'newPasswordConfirm',
  'token',
  'authorization',
]);

const sanitizeVariables = (variables: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(variables).map(([key, value]) => {
      if (SENSITIVE_KEYS.has(key)) return [key, '[REDACTED]'];
      if (value instanceof File || value instanceof Blob)
        return [key, `[File: ${(value as File).name ?? 'upload'}]`];
      if (Array.isArray(value))
        return [
          key,
          value.map(item =>
            item && typeof item === 'object'
              ? sanitizeVariables(item as Record<string, unknown>)
              : item,
          ),
        ];
      if (value && typeof value === 'object')
        return [key, sanitizeVariables(value as Record<string, unknown>)];
      return [key, value];
    }),
  );

const uploadLink = createUploadLink({
  uri: config.API_URL,
  credentials: 'include',
});

const authLink = setContext(async (_, { headers }) => {
  const token = getToken();
  const deviceInfo = deviceDetect(navigator.userAgent);
  let deviceInfoData = {};
  if (deviceInfo.vendor && deviceInfo.model) {
    deviceInfoData = {
      'em-system-version': deviceInfo.osVersion,
      'em-device-manufacturer': deviceInfo.vendor,
      'em-device-model': deviceInfo.model,
    };
  }

  const fbp = cookie.load('_fbp');
  const fbc = cookie.load('_fbc');
  let facebookData = {};
  if (fbp) {
    facebookData = { 'em-facebook-fbp': fbp };
  }
  if (fbc) {
    facebookData = { ...facebookData, 'em-facebook-fbc': fbc };
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'em-platform': getBrowserPlatform(),
      'em-browser': getBrowserVersion(),
      'em-channel': 'browser',
      'em-device-type': 'Web',
      ...deviceInfoData,
      ...facebookData,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  handleApolloOperationError({
    graphQLErrors,
    networkError,
    operationName: operation.operationName,
    variables: sanitizeVariables(operation.variables),
  });
});

const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(uploadLink)),
  cache: new InMemoryCache(),
});

export default client;
